import React, { useEffect, useState, createContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { Clock } from "lucide-react";
import { Button } from "./components/ui/button";
import axios from "axios";
import Landing from "./pages/Landing";
import DashboardDuolingo from "./pages/DashboardDuolingo";
import Exam from "./pages/Exam";
import Results from "./pages/Results";
import Admin from "./pages/Admin";
import SpeakingPractice from "./pages/SpeakingPractice";
import SpeakingResults from "./pages/SpeakingResults";
import LevelUpTest from "./pages/LevelUpTest";
import ReadingTest from "./pages/ReadingTest";
import WritingTest from "./pages/WritingTest";
import ListeningTest from "./pages/ListeningTest";
import ReadingResults from "./pages/ReadingResults";
import WritingResults from "./pages/WritingResults";
import ListeningResults from "./pages/ListeningResults";
import ExercisesDuolingo from "./pages/ExercisesDuolingo";
import Exercises from "./pages/Exercises";
import GrammarExerciseDuolingo from "./pages/GrammarExerciseDuolingo";
import GrammarLesson from "./pages/GrammarLesson";
import TensesOverview from "./pages/TensesOverview";
import VocabularyExerciseDuolingo from "./pages/VocabularyExerciseDuolingo";
import ExerciseResultsDuolingo from "./pages/ExerciseResultsDuolingo";
import Profile from "./pages/Profile";
import Lessons from "./pages/Lessons";
import Lesson from "./pages/Lesson";
import Gamification from "./pages/Gamification";
import CabinCrewModules from "./pages/CabinCrewModules";
import CabinCrewLesson from "./pages/CabinCrewLesson";
import CabinCrewComprehensive from "./pages/CabinCrewComprehensive";
import CabinCrewDashboard from "./pages/CabinCrewDashboard";
import CabinCrewFlashcards from "./pages/CabinCrewFlashcards";
import CabinCrewDragDrop from "./pages/CabinCrewDragDrop";
import CabinCrewTimedChallenge from "./pages/CabinCrewTimedChallenge";
import CabinCrewInterview from "./pages/CabinCrewInterview";
import CabinCrewRoleplay from "./pages/CabinCrewRoleplay";
import CabinInterviewPrep from "./pages/CabinInterviewPrep";
import InterviewModuleDetail from "./pages/InterviewModuleDetail";
import InterviewPracticeHub from "./pages/InterviewPracticeHub";
import InterviewQuizPractice from "./pages/InterviewQuizPractice";
import InterviewTimedPractice from "./pages/InterviewTimedPractice";
import CabinCrewEmergency from "./pages/CabinCrewEmergency";
import CabinCrewVoicePractice from "./pages/CabinCrewVoicePractice";
import CabinCrewProgress from "./pages/CabinCrewProgress";
import CabinCrewCareerCV from "./pages/CabinCrewCareerCV";
import CabinCrewCareerFitness from "./pages/CabinCrewCareerFitness";
import CabinCrewCareerGrooming from "./pages/CabinCrewCareerGrooming";
import CabinCrewAirlines from "./pages/CabinCrewAirlines";
import JobListings from "./pages/JobListings";
import CabinCrewAchievements from "./pages/CabinCrewAchievements";
import CabinCrewRoadmap from "./pages/CabinCrewRoadmap";
import AgreementModal from "./components/AgreementModal";
import CabinCrewMyExams from "./pages/CabinCrewMyExams";
import LearningPath from "./pages/LearningPath";
import PlacementTest from "./pages/PlacementTest";
import ProgramDashboard from "./pages/ProgramDashboard";
import ProgramSection from "./pages/ProgramSection";
import ProgramLesson from "./pages/ProgramLesson";
import DuolingoHome from "./pages/DuolingoHome";
import DuolingoSection from "./pages/DuolingoSection";
import DuolingoLesson from "./pages/DuolingoLesson";
import KariyerHome from "./pages/KariyerHome";
import LevelSkillTest from "./pages/LevelSkillTest";
import MySpeakingRecordings from "./pages/MySpeakingRecordings";
import KariyerLevel from "./pages/KariyerLevel";
import AdminDashboard from "./pages/AdminDashboard";
import ActivityLogs from "./pages/ActivityLogs";
import AIInterview from "./pages/AIInterview";
import MainMenu from "./pages/MainMenu";
import Podcasts from "./pages/Podcasts";
import AiVoiceInterview from "./pages/AiVoiceInterview";
import CabinTrainingHome from "./pages/CabinTrainingHome";
import CabinModuleDetail from "./pages/CabinModuleDetail";
import CabinLesson from "./pages/CabinLesson";
import LeaderboardPage from "./pages/LeaderboardPage";
import SpinWheel from "./pages/SpinWheel";
import FlightTickets from "./pages/FlightTickets";
import DailyTasks from "./pages/DailyTasks";
import CareerLevels from "./pages/CareerLevels";
import VideoLessons from "./pages/VideoLessons";
import CertificateVerify from "./pages/CertificateVerify";
import VideoInterview from "./pages/VideoInterview";
import PsychologyTests from "./pages/PsychologyTests";
import { Toaster } from "./components/ui/sonner";
// Wrapper component to force re-render on route param changes
function DuolingoLessonWrapper() {
  const { levelId, unitId, lessonId } = useParams();
  // key prop forces React to unmount and remount the component when params change
  return <DuolingoLesson key={`${levelId}-${unitId}-${lessonId}`} />;
}
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Global axios interceptor to add auth token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// Response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on auth endpoints to prevent loops
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Token expired or invalid - clear and redirect will happen via ProtectedRoute
      localStorage.removeItem('session_token');
    }
    return Promise.reject(error);
  }
);
export const AuthContext = createContext(null);
const ProtectedRoute = ({ children, skipPlacementCheck = false }) => {
  const { user, loading, setUser } = React.useContext(AuthContext);
  const location = useLocation();
  const [showAgreements, setShowAgreements] = React.useState(false);
  
  // Sözleşme kabul kontrolü
  React.useEffect(() => {
    if (user && !user.agreements_accepted && user.role !== 'admin') {
      setShowAgreements(true);
    }
  }, [user]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  // Sözleşme kabul modalı
  if (showAgreements && user.role !== 'admin') {
    return (
      <AgreementModal 
        isOpen={true} 
        user={user}
        onAccept={() => {
          setShowAgreements(false);
          setUser({ ...user, agreements_accepted: true });
        }}
      />
    );
  }
  
  // Admin kullanıcılar placement test'ten muaf
  if (user.role === 'admin') {
    return children;
  }
  
  // Placement test sayfasına erişime izin ver
  if (skipPlacementCheck || location.pathname === '/placement-test') {
    return children;
  }
  
  // Placement test tamamlanmamışsa yönlendir
  if (!user.placement_test_completed) {
    return <Navigate to="/placement-test" replace />;
  }
  
  // Demo süresi dolduysa engelle
  if (user.demo_expired) {
    const [messageSent, setMessageSent] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    
    const sendInterestMessage = async () => {
      setSending(true);
      try {
        await axios.post(`${API}/messages/demo-interest`, {
          user_id: user.id,
          user_name: user.name,
          user_email: user.email,
          message: "Eğitime katılmak istiyorum"
        }, { withCredentials: true });
        setMessageSent(true);
      } catch (error) {
        console.error('Mesaj gönderilemedi');
      } finally {
        setSending(false);
      }
    };
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-yellow-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Demo Süreniz Doldu</h1>
          <p className="text-gray-600 mb-6">
            8 saatlik demo süreniz sona erdi. Eğitime devam etmek ister misiniz?
          </p>
          <div className="space-y-3">
            {messageSent ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-green-700 font-medium">✅ Talebiniz alındı!</p>
                <p className="text-green-600 text-sm mt-1">En kısa sürede sizinle iletişime geçeceğiz.</p>
              </div>
            ) : (
              <Button 
                className="w-full bg-green-600 hover:bg-green-700" 
                onClick={sendInterestMessage}
                disabled={sending}
              >
                {sending ? '📨 Gönderiliyor...' : '📩 Eğitime Katılmak İstiyorum'}
              </Button>
            )}
            <Button variant="outline" className="w-full" onClick={() => { localStorage.clear(); window.location.href = '/'; }}>
              Çıkış Yap
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
};
const SessionHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setLoading } = React.useContext(AuthContext);
  
  useEffect(() => {
    const handleSession = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setLoading(true);
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            {},
            {
              headers: { 'X-Session-ID': sessionId },
              withCredentials: true
            }
          );
          
          // Save session token to localStorage for persistent auth
          if (response.data.session_token) {
            localStorage.setItem('session_token', response.data.session_token);
          } else if (sessionId) {
            localStorage.setItem('session_token', sessionId);
          }
          
          setUser(response.data.user);
          window.history.replaceState({}, document.title, '/dashboard');
          navigate('/dashboard');
        } catch (error) {
          console.error('Session error:', error);
          localStorage.removeItem('session_token');
        } finally {
          setLoading(false);
        }
      }
    };
    
    handleSession();
  }, []);  // Only run once on mount, not on every location change
  
  return null;
};
// Aktivite izleyici - sayfa değişikliklerini takip eder
const ActivityTracker = () => {
  const location = useLocation();
  const { user } = React.useContext(AuthContext);
  
  useEffect(() => {
    if (user) {
      // Sayfa görüntüleme aktivitesini gönder
      const trackActivity = async () => {
        try {
          await axios.post(`${API}/track/activity`, {
            page: location.pathname,
            action: 'page_view'
          }, { withCredentials: true });
        } catch (e) {
          // Sessizce başarısız ol
        }
      };
      trackActivity();
    }
  }, [location.pathname, user]);
  
  return null;
};
// Global Sorun Bildir Butonu
const FeedbackButton = () => {
  const location = useLocation();
  const { user } = React.useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportType, setReportType] = useState('bug');
  const [sending, setSending] = useState(false);
  
  // Sadece giriş yapmış kullanıcılar için göster
  if (!user) return null;
  
  // Admin panel ve login sayfasında gösterme
  if (location.pathname.startsWith('/admin') || location.pathname === '/') return null;
  
  const handleSubmit = async () => {
    if (!reportText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/feedback/report`, {
        type: reportType,
        message: reportText,
        page: location.pathname,
        user_agent: navigator.userAgent
      }, { withCredentials: true });
      alert('Bildiriminiz alındı, teşekkürler!');
      setShowModal(false);
      setReportText('');
    } catch (err) {
      alert('Bildirim gönderilemedi');
    }
    setSending(false);
  };
  
  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-all hover:scale-105"
        title="Sorun Bildir"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <span className="hidden sm:inline text-sm font-medium">Sorun Bildir</span>
      </button>
      
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-700 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Sorun Bildir
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-white/50 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              {/* Sorun Türü */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Sorun Türü</label>
                <div className="flex gap-2">
                  {[
                    { id: 'bug', label: '🐛 Hata' },
                    { id: 'suggestion', label: '💡 Öneri' },
                    { id: 'question', label: '❓ Soru' }
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => setReportType(type.id)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        reportType === type.id
                          ? 'bg-blue-500 text-white'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Açıklama */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Açıklama</label>
                <textarea
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  placeholder="Sorunu veya önerinizi detaylı açıklayın..."
                  className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="4"
                />
              </div>
              {/* Sayfa Bilgisi */}
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-white/50 text-xs">📍 Bulunduğunuz sayfa: {location.pathname}</p>
              </div>
              {/* Gönder Butonu */}
              <button
                onClick={handleSubmit}
                disabled={sending || !reportText.trim()}
                className="w-full py-3 bg-red-500 hover:bg-red-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all"
              >
                {sending ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Logout fonksiyonu
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Sessizce başarısız ol
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };
  
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('session_token');
      
      // No token means not authenticated
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.log('Not authenticated or token expired');
        localStorage.removeItem('session_token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  return (
    <AuthContext.Provider value={{ user, setUser, loading, setLoading, logout }}>
      <div className="App">
        <BrowserRouter>
          <SessionHandler />
          <ActivityTracker />
          <FeedbackButton />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/verify/:code" element={<CertificateVerify />} />
            
            <Route path="/placement-test"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <PlacementTest />
                </ProtectedRoute>
              }
            />
            
            <Route path="/dashboard"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <MainMenu />
                </ProtectedRoute>
              }
            />
            
            
            <Route path="/podcasts"
              element={
                <ProtectedRoute>
                  <Podcasts />
                </ProtectedRoute>
              }
            />
            <Route path="/english-training"
              element={
                <ProtectedRoute>
                  <KariyerHome />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-training"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <CabinTrainingHome />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-training/module/:moduleId"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <CabinModuleDetail />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-training/lesson/:lessonId"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <CabinLesson />
                </ProtectedRoute>
              }
            />
            
            <Route path="/level/:levelId"
              element={
                <ProtectedRoute>
                  <KariyerLevel />
                </ProtectedRoute>
              }
            />
            
            <Route path="/lesson/:levelId/:unitId/:lessonId"
              element={
                <ProtectedRoute>
                  <DuolingoLessonWrapper />
                </ProtectedRoute>
              }
            />
            
            <Route path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route path="/interview-practice-hub"
              element={
                <ProtectedRoute>
                  <InterviewPracticeHub />
                </ProtectedRoute>
              }
            />
            
            <Route path="/interview-prep"
              element={
                <ProtectedRoute>
                  <CabinInterviewPrep />
                </ProtectedRoute>
              }
            />
            
            <Route path="/interview-prep/practice"
              element={
                <ProtectedRoute>
                  <InterviewQuizPractice />
                </ProtectedRoute>
              }
            />
            
            <Route path="/interview-prep/timed"
              element={
                <ProtectedRoute>
                  <InterviewTimedPractice />
                </ProtectedRoute>
              }
            />
            
            <Route path="/interview-prep/module/:moduleId"
              element={
                <ProtectedRoute>
                  <InterviewModuleDetail />
                </ProtectedRoute>
              }
            />
            
            <Route path="/interview-prep/lesson/:lessonId"
              element={
                <ProtectedRoute>
                  <CabinLesson />
                </ProtectedRoute>
              }
            />
            
            <Route path="/ai-interview"
              element={
                <ProtectedRoute>
                  <AIInterview />
                </ProtectedRoute>
              }
            />
            
            <Route path="/ai-voice-interview"
              element={
                <ProtectedRoute>
                  <AiVoiceInterview />
                </ProtectedRoute>
              }
            />
            
            <Route path="/leaderboard"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <LeaderboardPage />
                </ProtectedRoute>
              }
            />
            
            <Route path="/old-duolingo"
              element={
                <ProtectedRoute>
                  <DuolingoHome />
                </ProtectedRoute>
              }
            />
            
            <Route path="/learn/:sectionId"
              element={
                <ProtectedRoute>
                  <DuolingoSection />
                </ProtectedRoute>
              }
            />
            
            <Route path="/old-dashboard"
              element={
                <ProtectedRoute>
                  <ProgramDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route path="/program/:sectionId"
              element={
                <ProtectedRoute>
                  <ProgramSection />
                </ProtectedRoute>
              }
            />
            
            <Route path="/program/:sectionId/lesson/:unitId/:lessonId"
              element={
                <ProtectedRoute>
                  <ProgramLesson />
                </ProtectedRoute>
              }
            />
            
            <Route path="/old-dashboard"
              element={
                <ProtectedRoute>
                  <CabinCrewDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route path="/languages"
              element={
                <ProtectedRoute>
                  <DashboardDuolingo />
                </ProtectedRoute>
              }
            />
            
            <Route path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            
            <Route path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            
            <Route path="/admin/activity-logs"
              element={
                <ProtectedRoute>
                  <ActivityLogs />
                </ProtectedRoute>
              }
            />
            
            <Route path="/exam"
              element={
                <ProtectedRoute>
                  <Exam />
                </ProtectedRoute>
              }
            />
            
            <Route path="/exam/:language"
              element={
                <ProtectedRoute>
                  <Exam />
                </ProtectedRoute>
              }
            />
            
            <Route path="/results/:resultId"
              element={
                <ProtectedRoute>
                  <Results />
                </ProtectedRoute>
              }
            />
            
            <Route path="/speaking-practice"
              element={
                <ProtectedRoute>
                  <SpeakingPractice />
                </ProtectedRoute>
              }
            />
            
            <Route path="/speaking-results/:sessionId"
              element={
                <ProtectedRoute>
                  <SpeakingResults />
                </ProtectedRoute>
              }
            />
            
            <Route path="/level-up-test"
              element={
                <ProtectedRoute>
                  <LevelUpTest />
                </ProtectedRoute>
              }
            />
            
            <Route path="/reading-test"
              element={
                <ProtectedRoute>
                  <ReadingTest />
                </ProtectedRoute>
              }
            />
            
            <Route path="/writing-test"
              element={
                <ProtectedRoute>
                  <WritingTest />
                </ProtectedRoute>
              }
            />
            
            <Route path="/listening-test"
              element={
                <ProtectedRoute>
                  <ListeningTest />
                </ProtectedRoute>
              }
            />
            
            <Route path="/reading-results/:sessionId"
              element={
                <ProtectedRoute>
                  <ReadingResults />
                </ProtectedRoute>
              }
            />
            
            <Route path="/writing-results/:sessionId"
              element={
                <ProtectedRoute>
                  <WritingResults />
                </ProtectedRoute>
              }
            />
            
            <Route path="/listening-results/:sessionId"
              element={
                <ProtectedRoute>
                  <ListeningResults />
                </ProtectedRoute>
              }
            />
            
            <Route path="/skill-test/:level/:type"
              element={
                <ProtectedRoute>
                  <LevelSkillTest />
                </ProtectedRoute>
              }
            />
            
            <Route path="/my-speaking-recordings"
              element={
                <ProtectedRoute>
                  <MySpeakingRecordings />
                </ProtectedRoute>
              }
            />
            
            <Route path="/spin-wheel"
              element={
                <ProtectedRoute>
                  <SpinWheel />
                </ProtectedRoute>
              }
            />
            
            <Route path="/flight-tickets"
              element={
                <ProtectedRoute>
                  <FlightTickets />
                </ProtectedRoute>
              }
            />
            
            <Route path="/flight-routes"
              element={
                <ProtectedRoute>
                  <FlightTickets />
                </ProtectedRoute>
              }
            />
            
            <Route path="/daily-tasks"
              element={
                <ProtectedRoute>
                  <DailyTasks />
                </ProtectedRoute>
              }
            />
            
            <Route path="/career-levels"
              element={
                <ProtectedRoute>
                  <CareerLevels />
                </ProtectedRoute>
              }
            />
            
            <Route path="/video-interview"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <VideoInterview />
                </ProtectedRoute>
              }
            />
            
            <Route path="/psychology-tests"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <PsychologyTests />
                </ProtectedRoute>
              }
            />
            
            <Route path="/video-lessons"
              element={
                <ProtectedRoute skipPlacementCheck={true}>
                  <VideoLessons />
                </ProtectedRoute>
              }
            />
            
            <Route path="/exercises"
              element={
                <ProtectedRoute>
                  <Exercises />
                </ProtectedRoute>
              }
            />
            
            <Route path="/learning-path"
              element={
                <ProtectedRoute>
                  <LearningPath />
                </ProtectedRoute>
              }
            />
            
            <Route path="/exercises/grammar/:level/:topic"
              element={
                <ProtectedRoute>
                  <GrammarExerciseDuolingo />
                </ProtectedRoute>
              }
            />
            
            <Route path="/tenses"
              element={
                <ProtectedRoute>
                  <TensesOverview />
                </ProtectedRoute>
              }
            />
            
            <Route path="/grammar-lesson/:level/:topic"
              element={
                <ProtectedRoute>
                  <GrammarLesson />
                </ProtectedRoute>
              }
            />
            
            <Route path="/exercises/vocabulary/:level/:theme"
              element={
                <ProtectedRoute>
                  <VocabularyExerciseDuolingo />
                </ProtectedRoute>
              }
            />
            
            <Route path="/exercises/results/:sessionId"
              element={
                <ProtectedRoute>
                  <ExerciseResultsDuolingo />
                </ProtectedRoute>
              }
            />
            
            <Route path="/lessons"
              element={
                <ProtectedRoute>
                  <Lessons />
                </ProtectedRoute>
              }
            />
            
            <Route path="/lessons/:language"
              element={
                <ProtectedRoute>
                  <Lessons />
                </ProtectedRoute>
              }
            />
            
            <Route path="/lesson/:language/:level/:unitId"
              element={
                <ProtectedRoute>
                  <Lesson />
                </ProtectedRoute>
              }
            />
            
            <Route path="/lesson/:language/:level/:unitId/:topic"
              element={
                <ProtectedRoute>
                  <Lesson />
                </ProtectedRoute>
              }
            />
            
            <Route path="/gamification"
              element={
                <ProtectedRoute>
                  <Gamification />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew-dashboard"
              element={
                <ProtectedRoute>
                  <CabinCrewDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew-progress"
              element={
                <ProtectedRoute>
                  <CabinCrewProgress />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew"
              element={
                <ProtectedRoute>
                  <CabinCrewDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/modules"
              element={
                <ProtectedRoute>
                  <CabinCrewModules />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/lesson/:level/:moduleId"
              element={
                <ProtectedRoute>
                  <CabinCrewLesson />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/comprehensive/:level/:moduleId"
              element={
                <ProtectedRoute>
                  <CabinCrewComprehensive />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/comprehensive"
              element={
                <ProtectedRoute>
                  <Navigate to="/cabin-crew/modules" replace />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/flashcards"
              element={
                <ProtectedRoute>
                  <CabinCrewFlashcards />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/drag-drop"
              element={
                <ProtectedRoute>
                  <CabinCrewDragDrop />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/timed-challenge"
              element={
                <ProtectedRoute>
                  <CabinCrewTimedChallenge />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/interview"
              element={
                <ProtectedRoute>
                  <CabinCrewInterview />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/roleplay"
              element={
                <ProtectedRoute>
                  <CabinCrewRoleplay />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/emergency"
              element={
                <ProtectedRoute>
                  <CabinCrewEmergency />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/voice-practice"
              element={
                <ProtectedRoute>
                  <CabinCrewVoicePractice />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/progress"
              element={
                <ProtectedRoute>
                  <CabinCrewProgress />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/career/cv"
              element={
                <ProtectedRoute>
                  <CabinCrewCareerCV />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/career/fitness"
              element={
                <ProtectedRoute>
                  <CabinCrewCareerFitness />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/career/grooming"
              element={
                <ProtectedRoute>
                  <CabinCrewCareerGrooming />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/airlines"
              element={
                <ProtectedRoute>
                  <CabinCrewAirlines />
                </ProtectedRoute>
              }
            />
            
            <Route path="/jobs"
              element={<JobListings />}
            />
            
            <Route path="/cabin-crew/achievements"
              element={
                <ProtectedRoute>
                  <CabinCrewAchievements />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/roadmap"
              element={
                <ProtectedRoute>
                  <CabinCrewRoadmap />
                </ProtectedRoute>
              }
            />
            
            <Route path="/cabin-crew/my-exams"
              element={
                <ProtectedRoute>
                  <CabinCrewMyExams />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthContext.Provider>
  );
}
export default App;
// Build timestamp: 1765526163
